import { IconExpandMore } from '@tvg/fuse-icons';
import { StyledScreenReaderSpan, uuid } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { config, styledDropdownConfig } from './config';
import {
	StyledDescriptionContainer,
	StyledDropdownWrapper,
	StyledExpandIcon,
	StyledHelperText,
	StyledIcon,
	StyledIconWrapper,
	StyledLabel,
	StyledSelect,
	StyledWrapper,
} from './styled-dropdown';

const { componentName, borderColor } = styledDropdownConfig;

const { icon } = config;

const { forwardRef, useRef, useState, useCallback } = React;

const propTypes = {
	className: PropTypes.string,
	csMask: PropTypes.bool,
	dataTestId: PropTypes.string,
	defaultOption: PropTypes.string,
	helperText: PropTypes.string,
	isRequired: PropTypes.bool,
	label: PropTypes.string,
	placeholder: PropTypes.string,
	onChange: PropTypes.func,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
		}).isRequired,
	),
	size: PropTypes.string,
	state: PropTypes.string,
	componentId: PropTypes.string,
};

const defaultProps = {
	className: 'fuse-dropdown',
	csMask: false,
	dataTestId: 'fuse-dropdown-data-test-id',
	defaultOption: '',
	helperText: undefined,
	isRequired: false,
	label: undefined,
	placeholder: 'Show options',
	onChange: undefined,
	options: [],
	size: 'large',
	state: undefined,
	componentId: uuid(),
};

const Dropdown = forwardRef(
	(
		{
			className,
			csMask,
			dataTestId,
			defaultOption,
			helperText,
			isRequired,
			label,
			placeholder,
			onChange,
			options,
			size,
			state,
			componentId,
			...rest
		},
		ref,
	) => {
		const selectLabelId = `${componentName}-dropdown-${componentId}`;
		const helpTextId = `${componentName}-helper-text-${componentId}`;
		const dropdownId = `${componentName}-dropdown-icon-${componentId}`;
		const expandIconId = `${componentName}-dropdown-expand-icon-${componentId}`;

		// Setting initial select value based on prop
		const [dropdownValue, setDropDownValue] = useState(defaultOption);

		// Setting variable for disabled state
		const isDisabled = state === 'disabled';

		// Creating ID for helper message
		const helperTextId = (helperText && helpTextId) || '';

		// Creating ID for state icon (error/success icons)
		const dropdownStateIcon =
			(state && (state === 'error' || state === 'success') && dropdownId) || '';

		// Creating reference for select element
		const dropdownContainerRef = useRef();
		const dropdownSelectRef = useRef();

		// Handler to programmtically remove focus outline only when select has focus and is tabbed
		const handleContainerBlur = useCallback(() => {
			dropdownContainerRef.current.classList.remove(`${componentName}__wrapper--outline`);
		}, []);

		// Handler to programmtically set focus outline only when select has focus and is tabbed
		const handleContainerKeyUp = useCallback(({ keyCode }) => {
			const tabKey = 9;

			if (keyCode === tabKey && document.activeElement === dropdownSelectRef.current) {
				dropdownContainerRef.current.classList.add(`${componentName}__wrapper--outline`);
			}
		}, []);

		// Handler for on change of select element
		const handleSelectOnChange = useCallback((e) => {
			const selectValue = e.currentTarget.value;

			if (onChange) onChange(e);

			dropdownContainerRef.current.classList.add(`${componentName}__wrapper--selected`);

			setDropDownValue(selectValue);
		}, []);

		// Creating options components based on props & adding placeholder
		const getSelectOptions = useCallback((optionsArr) => {
			const placeHolderOption = [
				{
					className: `${componentName}__option ${componentName}__option--placeholder`,
					label: placeholder,
					value: '',
					disabled: true,
				},
			];

			const selectOptions = [...(defaultOption === '' ? placeHolderOption : []), ...optionsArr];

			return selectOptions.map((option, index) => {
				const { label: optionLabel, value, name, ...optionProps } = option;

				const optionId = `${componentId}__option_${index}`;

				return (
					<option
						className={`${componentName}__option`}
						key={optionId}
						name={name}
						value={value}
						{...optionProps}
					>
						{optionLabel}
					</option>
				);
			});
		}, []);

		return (
			<StyledWrapper
				ref={ref}
				className={`${className}${isDisabled ? ' fuse-dropdown--disabled' : ''}`}
				data-testid={dataTestId}
			>
				{label && (
					<StyledLabel htmlFor={selectLabelId} size={size}>
						{label}
						{isRequired && ' *'}
					</StyledLabel>
				)}

				<StyledDropdownWrapper
					state={state}
					size={size}
					ref={dropdownContainerRef}
					onBlur={handleContainerBlur}
					onKeyUp={handleContainerKeyUp}
				>
					<StyledSelect
						id={selectLabelId}
						aria-describedby={`${expandIconId} ${dropdownStateIcon} ${helperTextId}`}
						aria-disabled={isDisabled}
						aria-invalid={state === 'error' ? true : undefined}
						aria-required={isRequired}
						disabled={isDisabled}
						isInDefaultState={state !== 'error' && state !== 'success'}
						size={size}
						data-cs-mask={csMask || undefined}
						onChange={handleSelectOnChange}
						ref={dropdownSelectRef}
						value={dropdownValue}
						{...rest}
					>
						{getSelectOptions(options)}
					</StyledSelect>

					<StyledIconWrapper>
						{icon?.[state] && (
							<StyledIcon
								color={borderColor?.[state]}
								icon={icon?.[state].state}
								id={dropdownStateIcon}
								srLabel={icon?.[state].label}
								responsiveSize={icon.size}
							/>
						)}

						<StyledExpandIcon
							icon={<IconExpandMore />}
							id={expandIconId}
							srLabel=""
							responsiveSize={icon.size}
						/>
					</StyledIconWrapper>
				</StyledDropdownWrapper>

				{helperText?.length > 0 && (
					<StyledDescriptionContainer>
						<StyledHelperText id={helperTextId} type="paragraph" size="m1" state={state} tag="p">
							{(state === 'error' || state === 'success') && (
								<StyledScreenReaderSpan>{state}: </StyledScreenReaderSpan>
							)}
							{helperText}
						</StyledHelperText>
					</StyledDescriptionContainer>
				)}
			</StyledWrapper>
		);
	},
);

Dropdown.propTypes = propTypes;
Dropdown.defaultProps = defaultProps;

export default Dropdown;

export { Dropdown };
